import React, { Fragment } from 'react';
import { SortOrder } from '@kirin/api-client/contracts/helpers';
import { MCP_FILTERABLE_FIELDS } from '@kirin/api-client/contracts/mcp';
import { FilterOperators } from '@kirin/api-client/contracts/table';
import { DashboardWidgetWrapper } from '@/pages/organization/components/DashboardWidgetWrapper';
import { MCPUsageUserList } from '@/pages/organization/components/WidgetUserLists/MCPUsageUserList.tsx';
import { useParams } from 'react-router-dom';
import { useMCPServers } from '@/hooks/api/useMCPServers';
import type { MCPServerUsage } from './types';
import { UsageRow } from '@/pages/organization/components/UsageRow';

const buildMCPUsageStats = (
  servers: { name: string; userCount: number }[]
): MCPServerUsage[] => {
  if (!servers.length) return [];

  // Use the highest server count as the total (for percentage calculations)
  const maxCount = Math.max(...servers.map(s => s.userCount));

  return servers.map(server => ({
    id: server.name,
    name: server.name,
    usage: server.userCount,
    total: maxCount,
  }));
};

const LIMIT = 10;

/**
 * Endpoint rows only (KIR-4289).
 *
 * This widget ranks servers BY NUMBER OF USERS, and a gateway-provenance row
 * has no per-user attribution at all: the list sends `userCount: null` for it.
 * Letting one in produces three separate wrongs — it ranks as "0 users" beside
 * real counts, it collides React keys with a same-named endpoint server (rows
 * are keyed by name here, not by row id), and clicking it opens the ENDPOINT
 * server's user list because the drilldown looks the server up by name too.
 *
 * `provenance` is a branch selector rather than a predicate, so this costs the
 * list nothing: it skips the gateway branch instead of filtering its rows out.
 */
const ENDPOINT_ONLY = [
  {
    field: MCP_FILTERABLE_FIELDS.PROVENANCE,
    operator: FilterOperators.INCLUDES,
    value: ['endpoint'],
  },
];

export const MCPServersUsageWidget: React.FC = () => {
  const { organizationId } = useParams<{ organizationId: string }>();
  const { data, isLoading, isError, error, refetch } = useMCPServers(
    organizationId,
    {
      sortBy: 'userCount',
      sortOrder: SortOrder.Desc,
      limit: LIMIT,
      filters: ENDPOINT_ONLY,
    }
  );

  const usageData: MCPServerUsage[] = buildMCPUsageStats(
    data?.data?.map((s: { name: string; userCount: number | null }) => ({
      name: s.name,
      // KIR-3940: a null count means "not attributed", not "zero users". The
      // ENDPOINT_ONLY pin above is what keeps those rows out; this coalesce is
      // the type floor behind it, not the thing doing the work.
      userCount: s.userCount ?? 0,
    })) || []
  );

  const total = usageData[0]?.total ?? 0;
  const [selectedServer, setSelectedServer] =
    React.useState<MCPServerUsage | null>(null);

  const handleRowClick = (server: MCPServerUsage) => {
    setSelectedServer(server);
  };

  const handleCloseModal = () => {
    setSelectedServer(null);
  };

  return (
    <Fragment>
      <DashboardWidgetWrapper
        className="xl:col-span-8 min-h-0"
        cardClassName="xl:h-full"
        title="Top MCP Servers by Usage"
        description="Shows up to 10 MCP Servers"
        isLoading={isLoading}
        isError={isError || !!error}
        refetch={refetch}
      >
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden space-y-4 pr-2">
          {usageData.map(server => (
            <UsageRow
              key={server.id}
              id={server.id}
              name={server.name}
              usage={server.usage}
              total={total}
              onClick={() => handleRowClick(server)}
            />
          ))}
        </div>
      </DashboardWidgetWrapper>
      <MCPUsageUserList
        organizationId={organizationId || ''}
        serverName={selectedServer ? selectedServer.name : ''}
        open={selectedServer !== null}
        onClose={handleCloseModal}
      />
    </Fragment>
  );
};
